import { useState } from 'react';
import styles from './ConflictModal.module.css';
import { VersionConflictError, ActiveEditor } from '../../services/versionControlService';

interface ConflictModalProps {
  conflict: VersionConflictError;
  yourChanges?: Record<string, any>;
  onReload: () => void;
  onOverride: () => void;
  onCancel: () => void;
}

export function ConflictModal({ conflict, yourChanges, onReload, onOverride, onCancel }: ConflictModalProps) {
  const [showDiff, setShowDiff] = useState(true);

  const getChangedFields = () => {
    if (!yourChanges || !conflict.currentData) return [];

    const changes: Array<{ field: string; yourValue: any; currentValue: any }> = [];

    Object.keys(yourChanges).forEach((key) => {
      if (
        key !== 'id' &&
        key !== 'version' &&
        key !== 'last_modified_by' &&
        key !== 'last_modified_at' &&
        yourChanges[key] !== conflict.currentData[key]
      ) {
        changes.push({
          field: key,
          yourValue: yourChanges[key],
          currentValue: conflict.currentData[key],
        });
      }
    });

    return changes;
  };

  const changedFields = getChangedFields();

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <svg className={styles.warningIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className={styles.title}>Version Conflict Detected</h2>
        </div>

        <p className={styles.message}>{conflict.message}</p>

        <div className={styles.conflictInfo}>
          <p>
            <strong>Last Modified By:</strong> {conflict.lastModifiedBy}
          </p>
          <p>
            <strong>Last Modified:</strong> {new Date(conflict.lastModifiedAt).toLocaleString()}
          </p>
          <p>
            <strong>Current Version:</strong> {conflict.currentVersion} (You were editing version{' '}
            {conflict.attemptedVersion})
          </p>
        </div>

        {changedFields.length > 0 && showDiff && (
          <div className={styles.diffSection}>
            <div className={styles.diffTitle}>Changes Comparison</div>
            <div className={styles.diffContainer}>
              {changedFields.map((change) => (
                <div key={change.field} className={styles.diffItem}>
                  <div className={styles.diffField}>{formatFieldName(change.field)}</div>
                  <div>
                    <div className={styles.diffValue + ' ' + styles.diffOld}>
                      Current: {formatValue(change.currentValue)}
                    </div>
                    <div className={styles.diffValue + ' ' + styles.diffNew}>
                      Your Change: {formatValue(change.yourValue)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {changedFields.length === 0 && (
          <div className={styles.noChanges}>
            No conflicting changes detected. The record was updated but your changes don't conflict.
          </div>
        )}

        <div className={styles.actions}>
          <button className={`${styles.button} ${styles.buttonSecondary}`} onClick={onCancel}>
            Cancel
          </button>
          <button className={`${styles.button} ${styles.buttonPrimary}`} onClick={onReload}>
            Reload & Start Over
          </button>
          <button className={`${styles.button} ${styles.buttonDanger}`} onClick={onOverride}>
            Override Changes
          </button>
        </div>
      </div>
    </div>
  );
}

interface ActiveEditorBannerProps {
  editors: ActiveEditor[];
  currentUserId: string;
}

export function ActiveEditorBanner({ editors, currentUserId }: ActiveEditorBannerProps) {
  const otherEditors = editors.filter((e) => e.user_id !== currentUserId);

  if (otherEditors.length === 0) return null;

  const editorNames = otherEditors.map((e) => e.display_name).join(', ');

  return (
    <div className={styles.editorBanner}>
      <svg className={styles.editorIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        />
      </svg>
      <div className={styles.editorText}>
        <span className={styles.editorName}>{editorNames}</span>{' '}
        {otherEditors.length === 1 ? 'is' : 'are'} currently editing this record
      </div>
    </div>
  );
}

function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
